import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";
import { useAuthStore } from "@/stores/authStore";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Youtube, Upload, User as UserIcon, Mail, Unplug, CheckCircle2, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { Sidebar } from "@/components/Sidebar";

const profileSchema = z.object({
  name: z.string().min(2, "Full name must be at least 2 characters").max(100),
});

type ProfileFormValues = z.infer<typeof profileSchema>;

export default function ProfilePage() {
  const { user, checkAuth } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  const [isProfilePhotoUploading, setIsProfilePhotoUploading] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [localPreviews, setLocalPreviews] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  // Social media handles state (similar to onboarding)
  const [instagram, setInstagram] = useState("");
  const [tiktok, setTiktok] = useState("");
  const [spotify, setSpotify] = useState("");
  const [applePodcast, setApplePodcast] = useState("");

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      name: user?.name || "",
    },
  });

  // Check if YouTube is connected via OAuth
  const isYouTubeConnected = !!user?.youtube_data;
  const [isFormInitialized, setIsFormInitialized] = useState(false);

  // Sync form with user data only on initial load
  useEffect(() => {
    if (user && !isFormInitialized) {
      form.reset({
        name: user.name || "",
      });

      // Initialize social media handles from existing profiles
      const profiles = user.social_profiles || [];
      const instagramProfile = profiles.find(p => p.platform === 'instagram');
      const tiktokProfile = profiles.find(p => p.platform === 'tiktok');
      const spotifyProfile = profiles.find(p => p.platform === 'spotify');
      const applePodcastProfile = profiles.find(p => p.platform === 'apple_podcasts');

      setInstagram(instagramProfile?.handle || "");
      setTiktok(tiktokProfile?.handle || "");
      setSpotify(spotifyProfile?.handle || "");
      setApplePodcast(applePodcastProfile?.handle || "");

      setIsFormInitialized(true);
    }
  }, [user, form, isFormInitialized]);

  const getImageUrl = (keyOrUrl: string) => {
    if (!keyOrUrl) return "";
    if (localPreviews[keyOrUrl]) return localPreviews[keyOrUrl];
    if (keyOrUrl.startsWith("blob:")) return keyOrUrl;
    if (keyOrUrl.startsWith("http")) return keyOrUrl;
    const baseUrl = import.meta.env.VITE_API_BASE_URL?.replace(/\/api\/v1\/?$/, '') || '';
    return `${baseUrl}/api/v1/integrations/upload/screenshot/view?key=${encodeURIComponent(keyOrUrl)}`;
  };

  const handleProfilePhotoUpload = async (file: File) => {
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type.toLowerCase())) {
      toast.error("Only JPG, JPEG, and PNG files are allowed.");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size cannot exceed 5MB.");
      return;
    }

    setIsProfilePhotoUploading(true);
    try {
      const s3_key = await apiClient.uploadProfilePhoto(file);

      // It can take a moment for the S3 object to be available.
      // We will poll for the image to become available before updating the user.
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Update user profile with the new photo key
      await apiClient.updateCurrentUser({
        profile_photo: s3_key
      });

      // Update local state for immediate feedback
      setLocalPreviews(prev => ({ ...prev, [s3_key]: URL.createObjectURL(file) }));

      // Refresh auth/user data to sync everything
      await checkAuth();
      toast.success("Profile photo updated");

    } catch (error) {
      console.error(error);
      toast.error("Failed to upload profile photo. Please ensure the file is a valid image and under 5MB.");
    } finally {
      setIsProfilePhotoUploading(false);
    }
  };

  const handleConnectYouTube = async () => {
    try {
      const { url } = await apiClient.connectYouTube();
      window.location.href = url;
    } catch (error) {
      console.error(error);
      toast.error("Failed to initiate YouTube connection");
    }
  };

  const handleDisconnectYouTube = async () => {
    if (!confirm("Are you sure you want to disconnect your YouTube channel? This will remove all associated data.")) {
      return;
    }

    try {
      await apiClient.disconnectYouTube();
      await checkAuth(); // Refresh user data
      toast.success("YouTube channel disconnected successfully");
    } catch (error) {
      console.error(error);
      toast.error("Failed to disconnect YouTube channel");
    }
  };

  const saveProfile = async () => {
    setIsLoading(true);
    const data = form.getValues();
    try {
      await apiClient.updateCurrentUser({
        name: data.name,
      });

      await checkAuth();
      toast.success("Profile updated successfully!");
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : "Failed to update profile");
    } finally {
      setIsLoading(false);
    }
  };

  const saveSocialProfiles = async () => {
    try {
      setIsSaving(true);

      // Prepare social profiles for backend (similar to onboarding)
      const socialProfiles: any[] = [];
      if (instagram) socialProfiles.push({ platform: "instagram", handle: instagram });
      if (tiktok) socialProfiles.push({ platform: "tiktok", handle: tiktok });
      if (spotify) socialProfiles.push({ platform: "spotify", handle: spotify });
      if (applePodcast) socialProfiles.push({ platform: "apple_podcasts", handle: applePodcast });

      await apiClient.updateCurrentUser({
        social_profiles: socialProfiles
      });

      await checkAuth(); // Refresh user data
      toast.success("Social profiles updated successfully!");
    } catch (error) {
      console.error("Error saving social profiles:", error);
      toast.error("Failed to save social profiles. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen overflow-hidden selection:bg-primary selection:text-primary-foreground relative bg-background text-foreground flex font-ibm">

      {/* Zone 1: Left Sidebar (Navigation) */}
      <Sidebar />

      {/* Content Wrapper (Main) */}
      <div
        id="main-wrapper"
        className="flex flex-1 pl-0 pt-16 md:pt-0 transition-all duration-300 h-screen"
      >
        {/* Zone 2: Main Content Area */}
        <main className="flex-1 min-w-0 flex flex-col z-10 overflow-y-auto no-scrollbar bg-transparent h-full py-6 px-4 md:pt-8 md:pr-10 md:pb-8 md:pl-10 relative">
          {/* Header Section */}
          <header className="flex flex-col md:flex-row mb-6 md:mb-10 items-center justify-between gap-4 md:gap-6">
            {/* Left Side: Profile & Context */}
            {/* <div className="flex flex-col md:flex-row items-center gap-6 text-center md:text-left">
              <div>
                <h1 className="text-3xl font-semibold text-foreground tracking-tight font-cabinet">
                  My Profile
                </h1>
                <p className="text-muted-foreground font-ibm text-sm mt-1 max-w-sm">
                  Manage your personal information and connected accounts.
                </p>
              </div>
            </div> */}

            {/* Right Side: Actions (Empty for now, could add explicit save or other actions) */}
            <div className="flex items-center gap-3 shrink-0">
            </div>
          </header>

          {/* Main Profile Content */}
          <div className="max-w-4xl mx-auto w-full">
            <div className="grid gap-8">
              {/* Personal Info Section */}
              <Card>
                <CardHeader>
                  <CardTitle>Personal Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col items-center gap-6 mb-8 sm:flex-row sm:items-center p-4 bg-gray-50/50 rounded-lg border border-dashed">
                    <div className="relative group">
                      <Avatar className="w-24 h-24 border-4 border-white shadow-sm">
                        <AvatarImage src={getImageUrl(user?.profile_photo || "")} className="object-cover" />
                        <AvatarFallback className="text-2xl bg-gray-100 text-gray-400">
                          {user?.name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <label className={`absolute inset-0 flex items-center justify-center bg-black/40 rounded-full cursor-pointer transition-all duration-200 ${isProfilePhotoUploading ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                        {isProfilePhotoUploading ? (
                          <div className="animate-spin w-6 h-6 border-2 border-white border-t-transparent rounded-full" />
                        ) : (
                          <Upload className="w-6 h-6 text-white" />
                        )}
                        <input
                          type="file"
                          accept=".jpg,.jpeg,.png"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleProfilePhotoUpload(file);
                            e.target.value = ''; // Reset input
                          }}
                          disabled={isProfilePhotoUploading}
                        />
                      </label>
                    </div>
                    <div className="flex-1 space-y-1 text-center sm:text-left">
                      <h3 className="font-semibold text-lg">Profile Photo</h3>
                      <p className="text-sm text-muted-foreground max-w-sm">
                        Upload a picture to make your profile stand out. Click the avatar to change it. (Max 5MB)
                      </p>
                    </div>
                  </div>

                  <Form {...form}>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <FormLabel className="text-sm font-medium">Email</FormLabel>
                        <div className="flex items-center space-x-2 p-2 bg-gray-50 rounded-md border text-gray-500">
                          <Mail className="w-4 h-4" />
                          <span>{user?.email}</span>
                        </div>
                      </div>

                      <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Full Name</FormLabel>
                            <FormControl>
                              <div className="flex items-center space-x-2">
                                <Input placeholder="Enter your full name" {...field} />
                                <Button
                                  size="sm"
                                  onClick={form.handleSubmit(saveProfile)}
                                  disabled={isLoading}
                                >
                                  Save
                                </Button>
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </Form>
                </CardContent>
              </Card>

              {/* Connected Accounts Section */}
              <Card>
                <CardHeader>
                  <CardTitle>Connected Accounts</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* YouTube OAuth Connection */}
                  <div className="space-y-4">
                    <div className="space-y-1">
                      <label className="text-base font-bold text-foreground">
                        Connect Your Platforms
                      </label>
                      <p className="text-sm text-muted-foreground leading-tight">
                        Connect your social media accounts to generate accurate Artie Scores and analytics.
                      </p>
                    </div>

                    <Button
                      className={`w-full h-14 text-lg font-medium rounded-xl shadow-sm transition-all flex items-center justify-start px-4 gap-3 ${isYouTubeConnected
                        ? "bg-green-50 border border-green-200 text-green-700 hover:bg-green-100"
                        : "bg-white hover:bg-gray-50 border border-gray-200 text-foreground"
                        }`}
                      onClick={isYouTubeConnected ? undefined : handleConnectYouTube}
                      disabled={isYouTubeConnected}
                    >
                      {isYouTubeConnected ? (
                        <>
                          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                          </div>
                          <div className="flex-1 text-left">
                            <div className="font-semibold">YouTube Connected</div>
                            <div className="text-sm text-green-600">{user?.youtube_data?.title}</div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-gray-400 hover:text-red-500"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDisconnectYouTube();
                            }}
                            title="Disconnect YouTube"
                          >
                            <Unplug className="w-4 h-4" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <div className="w-8 h-8 bg-[#FF0000] rounded-full flex items-center justify-center">
                            <Youtube className="w-4 h-4 text-white" />
                          </div>
                          Connect YouTube
                        </>
                      )}
                    </Button>
                  </div>

                  {/* Manual Social Handles */}
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground leading-tight">
                      Add your handles for other platforms:
                    </p>

                    {/* Instagram */}
                    <div className="relative">
                      <div className="absolute left-4 top-1/2 -translate-y-1/2">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" fill="#E1306C" />
                        </svg>
                      </div>
                      <Input
                        placeholder="Add Instagram handle here"
                        value={instagram}
                        onChange={(e) => setInstagram(e.target.value)}
                        className="bg-gray-50 border-gray-200 focus:border-primary focus:ring-primary h-12 text-base pl-12"
                      />
                    </div>

                    {/* TikTok */}
                    <div className="relative">
                      <div className="absolute left-4 top-1/2 -translate-y-1/2">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="text-black" xmlns="http://www.w3.org/2000/svg">
                          <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                        </svg>
                      </div>
                      <Input
                        placeholder="Add TikTok handle here"
                        value={tiktok}
                        onChange={(e) => setTiktok(e.target.value)}
                        className="bg-gray-50 border-gray-200 focus:border-primary focus:ring-primary h-12 text-base pl-12"
                      />
                    </div>

                    {/* Spotify */}
                    <div className="relative">
                      <div className="absolute left-4 top-1/2 -translate-y-1/2">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="#1DB954" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141 4.32-1.38 9.841-.719 13.44 1.56.42.24.6.841.301 1.261zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z" />
                        </svg>
                      </div>
                      <Input
                        placeholder="Add Spotify handle here"
                        value={spotify}
                        onChange={(e) => setSpotify(e.target.value)}
                        className="bg-gray-50 border-gray-200 focus:border-primary focus:ring-primary h-12 text-base pl-12"
                      />
                    </div>

                    {/* Apple Podcast */}
                    <div className="relative">
                      <div className="absolute left-4 top-1/2 -translate-y-1/2">
                        <svg width="20" height="20" viewBox="0 0 384 512" fill="currentColor" className="text-black" xmlns="http://www.w3.org/2000/svg">
                          <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 52.3-11.4 69.5-34.3z" />
                        </svg>
                      </div>
                      <Input
                        placeholder="Add Apple Podcast handle here"
                        value={applePodcast}
                        onChange={(e) => setApplePodcast(e.target.value)}
                        className="bg-gray-50 border-gray-200 focus:border-primary focus:ring-primary h-12 text-base pl-12"
                      />
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="flex justify-end pt-4">
                    <Button
                      onClick={saveSocialProfiles}
                      disabled={isSaving}
                      className="bg-black hover:bg-black/90 text-white rounded-xl px-8 h-12 shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSaving ? (
                        <>
                          Saving... <Loader2 className="ml-2 h-4 w-4 animate-spin" />
                        </>
                      ) : (
                        "Save Changes"
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </main>
      </div>

      {/* Image Preview Modal */}
      <Dialog open={!!previewImage} onOpenChange={() => setPreviewImage(null)}>
        <DialogContent className="max-w-4xl p-0 bg-transparent border-none shadow-none flex justify-center">
          {previewImage && (
            <img
              src={previewImage}
              alt="Preview"
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}